import ReceiptCamera from '../ReceiptCamera';
import { EXPENSE_CATEGORIES, PAYMENT_METHODS } from '../../utils/expenseHelpers';

/**
 * Add / Edit Expense modal. Pure presentation — all state lives in the
 * parent (Expenses.js); we only call the handlers it passes in.
 *
 * Extracted from Expenses.js as part of the giant-component refactor
 * (Feb 2026). Behavior is byte-for-byte equivalent.
 */
const ExpenseFormModal = ({
  open,
  editingExpense,
  formData,
  setFormData,
  onSubmit,
  onClose,
  onReceiptCapture,
}) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '2rem',
        }}
      >
        <h2
          style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem', color: '#1a1a1a' }}
          data-testid="expense-form-title"
        >
          {editingExpense ? 'Edit Expense' : 'Add New Expense'}
        </h2>

        <form onSubmit={onSubmit}>
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  data-testid="expense-amount-input"
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="expense-date-input"
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Merchant/Vendor *
              </label>
              <input
                type="text"
                required
                value={formData.merchant}
                onChange={(e) => setFormData({ ...formData, merchant: e.target.value })}
                placeholder="e.g., Amazon, Starbucks, Office Depot"
                data-testid="expense-merchant-input"
                style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  data-testid="expense-category-select"
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
                >
                  <option value="">Select Category</option>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  Payment Method
                </label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  data-testid="expense-payment-select"
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional notes about this expense"
                rows="3"
                style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical' }}
              />
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.tax_deductible}
                  onChange={(e) => setFormData({ ...formData, tax_deductible: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                Tax Deductible
              </label>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Receipt Image
              </label>
              <ReceiptCamera
                onCapture={onReceiptCapture}
                existingImage={formData.receipt_image}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button
              type="submit"
              data-testid="submit-expense-button"
              style={{
                flex: 1,
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              {editingExpense ? 'Update Expense' : 'Add Expense'}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="cancel-expense-button"
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseFormModal;
